import { LoanBriefInfo, ParentLoanDto } from '@/models/LoanDTO.model';

import { isEmpty } from 'lodash';
import * as yup from 'yup';

const loanIdValidation = (loansPerPlan: ParentLoanDto[]) =>
  yup
    .number()
    .required()
    .test(
      'valid-loan-id',
      'loan Id is not available in the plan',
      function (value) {
        if (!value) return false;
        if (!loansPerPlan) return false;
        return (
          Array.isArray(loansPerPlan) &&
          loansPerPlan.some(loan => loan.loanId === value)
        );
      }
    );

const dateValidation = yup
  .string()
  .optional()
  .test(
    'valid-month-year',
    'Date must be in MM/YYYY or M/YYYY format',
    value => {
      if (!value) return true;

      // Try MM/YYYY format
      const mmyyyyRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
      // Try M/YYYY format
      const myyyyRegex = /^([1-9]|1[0-2])\/\d{4}$/;

      if (!mmyyyyRegex.test(value) && !myyyyRegex.test(value)) {
        return false;
      }

      try {
        const [month, year] = value.split('/');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return (
          date instanceof Date &&
          !isNaN(date.getTime()) &&
          parseInt(year) >= 1900 &&
          parseInt(year) <= new Date().getFullYear() + 100
        );
      } catch {
        return false;
      }
    }
  );

const amountValidation = yup
  .number()
  .test(
    'valid-decimal',
    'Amount must be a positive number with up to 2 decimal places',
    value => {
      if (value === undefined) return true;
      return value > 0 && /^\d+(\.\d{0,2})?$/.test(value.toString());
    }
  );

const dateAmountValidation = (index: number) => {
  const dateField = `maxbalance${index}_date`;
  const amountField = `maxbalance${index}_amount`;

  return {
    [amountField]: amountValidation,
    [dateField]: dateValidation
  };
};

const validateDateAmountPairs = (value: any): boolean => {
  if (!value) return true;
  for (let i = 1; i <= 12; i++) {
    const hasDate = value[`maxbalance${i}_date`] !== undefined;
    const hasAmount = value[`maxbalance${i}_amount`] !== undefined;
    if ((hasDate && !hasAmount) || (!hasDate && hasAmount)) {
      return false;
    }
  }
  return true;
};

const validateAtLeastOnePair = (value: any): boolean => {
  if (!value || !value.loanId) return true;
  // If we have a loanId, check if at least one complete pair exists
  for (let i = 1; i <= 12; i++) {
    const hasDate = value[`maxbalance${i}_date`] !== undefined;
    const hasAmount = value[`maxbalance${i}_amount`] !== undefined;
    if (hasDate && hasAmount) {
      return true;
    }
  }
  return false;
};

export const loanHistoricalBalanceSchema = (
  loansPerPlan?: LoanBriefInfo
): any => {
  return yup
    .array()
    .of(
      yup.object().when({
        is: ({ ...row }) => !isEmpty(row),
        then: schema => {
          const shape = {
            loanId: loanIdValidation(loansPerPlan.data),
            ...dateAmountValidation(1),
            ...dateAmountValidation(2),
            ...dateAmountValidation(3),
            ...dateAmountValidation(4),
            ...dateAmountValidation(5),
            ...dateAmountValidation(6),
            ...dateAmountValidation(7),
            ...dateAmountValidation(8),
            ...dateAmountValidation(9),
            ...dateAmountValidation(10),
            ...dateAmountValidation(11),
            ...dateAmountValidation(12)
          };

          return schema
            .shape(shape)
            .test(
              'date-amount-pairs',
              'Date and amount must be provided together',
              validateDateAmountPairs
            )
            .test(
              'at-least-one-pair',
              'At least one date and amount pair is required',
              validateAtLeastOnePair
            );
        }
      })
    )
    .min(1);
};
